import {safeParseJson, debounce} from '../../js/tools';

export class Slider {
  /*
  On the site level, set the slider item sizes using `aspect-ratio`. It will look better.
  Use breakpoints to specify the number of items per row.
  */
  elements;
  state = {
    activeIndex: 0,
    loopItemIndexOffset: 0,
  };
  data = {
    mode: 'normal',
    items: [],

    // must be in descending order by `minWidth`
    breakpoints: [
      {minWidth: 900, visibleItems: 3},
      {minWidth: 600, visibleItems: 2},
      {minWidth: 0, visibleItems: 1},
    ],
  };

  constructor(element) {
    if (!element) {
      return;
    }

    this.elements = {
      root: element,
      title: element.querySelector('.title'), // optional
      leftArrow: element.querySelector('.left-arrow'),
      rightArrow: element.querySelector('.right-arrow'),
      itemSlider: element.querySelector('.item-slider'),
      itemContainer: element.querySelector('.item-container'),
      get items() { return element.querySelectorAll('.item-container .item') },
    };

    this.data = {
      ...this.data,
      ...safeParseJson(element.dataset.model),
    };

    // this.addTabIndexes();

    this.addEventListeners();
    this.updateActiveIndex();
    this.updateTitle();
    this.resizeItems();
    this.updateSliderOffset();

    this.onResizeDebounced = debounce(() => {
      this.resizeItems();
      this.positionItemGroups();
      this.updateActiveIndex();
      this.updateSliderOffset();
    });

    if (this.isLoopMode) {
      this.cloneItemsForLoop();
    }
  }

  get currentItemData() {
    return this.data.items[this.loopIndex];
  }

  get itemCount() {
    return this.elements.items.length;
  }

  get itemGroupItemCount() {
    /*
    Required for loop mode.
    */
    return this.elements.items.length / this.elements.root.querySelectorAll('.item-group').length;
  }

  get visibleItemCount() {
    let breakpoint;

    if (Array.isArray(this.data.breakpoints)) {
      breakpoint = this.data.breakpoints.find(breakpoint => document.documentElement.clientWidth > breakpoint.minWidth);
    }

    return breakpoint?.visibleItems || 1;
  }

  get minMaxIndex() {
    return {
      min: 0,
      max: this.itemCount - (this.visibleItemCount),
    };
  }

  get gapSize() {
    return Number.parseInt(window.getComputedStyle(this.elements.itemContainer).gap || 0);
  }

  get loopIndex() {
    if (!this.isLoopMode) {
      return this.state.activeIndex;
    }

    let loopIndex = this.state.activeIndex % this.itemGroupItemCount;

    if (loopIndex < 0) {
      loopIndex = this.itemGroupItemCount + loopIndex;
    }

    return loopIndex;
  }

  get isLoopMode() { return this.data.mode === 'loop' }

  resizeItems() {
    const itemSlider = this.elements.itemSlider;
    const totalGapSize = (this.visibleItemCount - 1) * this.gapSize;
    const width = (itemSlider.offsetWidth - totalGapSize) / this.visibleItemCount;
    const height = itemSlider.offsetHeight;
    this.elements.items.forEach(item => {
      item.style.width = `${width}px`;
      item.style.height = `${height}px`;
    });
  }

  positionItemGroups() {
    /*
    Must be done after resizing items.
    */
    const groups = this.elements.itemContainer.querySelectorAll('.item-group');
    if (!groups.length) {
      return;
    }

    const leftGroup = groups[0];
    const middleGroup = groups[1];
    const rightGroup = groups[2];
    const groupWidth = leftGroup.offsetWidth;
    const itemWidth = this.elements.items[0].offsetWidth + this.gapSize;
    middleGroup.style.left = `${(this.state.activeIndex - this.loopIndex) * itemWidth}px`;
    leftGroup.style.left = `${middleGroup.offsetLeft - groupWidth - this.gapSize}px`;
    rightGroup.style.left = `${middleGroup.offsetLeft + groupWidth + this.gapSize}px`;
  }

  addEventListeners() {
    this.elements.leftArrow.addEventListener('click', (event) => {
      this.slide('left');
    });

    this.elements.rightArrow.addEventListener('click', (event) => {
      this.slide('right');
    });

    window.addEventListener('resize', () => {
      this.onResizeDebounced();
    });
  }

  updateTitle() {
    if (this.elements.title && this.currentItemData?.title) {
      this.elements.title.innerHTML = this.currentItemData.title;
    }
  }

  updateSliderOffset() {
    const itemWidth = this.elements.items[0].offsetWidth + this.gapSize;
    const offset = this.state.activeIndex * itemWidth;
    this.elements.itemContainer.style.left = `${-offset}px`;
  }

  updateArrowButtonState() {
    this.elements.leftArrow.classList.remove('disabled');
    this.elements.rightArrow.classList.remove('disabled');

    if (this.state.activeIndex <= this.minMaxIndex.min) {
      this.elements.leftArrow.classList.add('disabled');
    }
    if (this.state.activeIndex >= this.minMaxIndex.max) {
      this.elements.rightArrow.classList.add('disabled');
    }
  }

  updateActiveIndex() {
    if (this.isLoopMode) {
      return;
    }

    if (this.state.activeIndex < this.minMaxIndex.min) {
      this.state.activeIndex = this.minMaxIndex.min;
    }
    if (this.state.activeIndex > this.minMaxIndex.max) {
      this.state.activeIndex = this.minMaxIndex.max;
    }

    this.updateArrowButtonState();
  }

  slide(direction) {
    console.log('slide', {direction, activeIndex: this.state.activeIndex});
    if (direction === 'left') {
      this.state.activeIndex -= this.visibleItemCount;
    } else if (direction === 'right') {
      this.state.activeIndex += this.visibleItemCount;
    }

    if (this.isLoopMode) {
      this.rotateItemGroups(direction);
    }

    this.updateActiveIndex();
    this.updateTitle();
    this.updateSliderOffset();
    console.log('slide...', {activeIndex: this.state.activeIndex});
  }

  cloneItemsForLoop() {
    const itemGroup = document.createElement('div');
    itemGroup.className = 'item-group';
    itemGroup.append(...this.elements.items);
    this.elements.itemContainer.append(itemGroup);

    const clone1 = itemGroup.cloneNode(true);
    const clone2 = itemGroup.cloneNode(true);
    clone1.classList.add('clone-1');
    clone2.classList.add('clone-2');
    this.elements.itemContainer.prepend(clone1);
    this.elements.itemContainer.append(clone2);

    const offset = itemGroup.offsetLeft + itemGroup.offsetWidth + this.gapSize;
    clone1.style.left = `${-offset}px`;
    clone2.style.left = `${offset}px`;
  }

  rotateItemGroups(direction) {
    console.log('rotateItemGroups', direction, {loopIndex: this.loopIndex});
    const firstGroup = this.elements.itemContainer.querySelector('.item-group');
    const lastGroup = this.elements.itemContainer.querySelector('.item-group:last-child');
    const groupWidth = firstGroup.offsetWidth;
    const maxLoopIndex = this.itemGroupItemCount - 1;
    const minLoopIndex = 0;

    if (direction === 'left' && this.loopIndex === maxLoopIndex) {
      const offset = firstGroup.offsetLeft - groupWidth - this.gapSize;
      lastGroup.style.left = `${offset}px`;
      this.elements.itemContainer.prepend(lastGroup);
    } else if (direction === 'right' && this.loopIndex === minLoopIndex) {
      const offset = lastGroup.offsetLeft + groupWidth + this.gapSize;
      firstGroup.style.left = `${offset}px`;
      this.elements.itemContainer.append(firstGroup);
    }
  }

  // addTabIndexes() {
  //   this.elements.root.querySelectorAll('.header').forEach(element => element.setAttribute('tabindex', 0));
  // }
}
